import type { ICommandBarItemProps } from '@fluentui/react';
import { IAppContext } from 'App/AppContext';
import type { AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import { TFunction } from 'i18next';
import { TenantProcessStateAnnualPlan } from 'models/copilot/Processes/AnnualPlan/tenantProcessStateAnnualPlan';
import { TenantProcessStateAuditFindings } from 'models/copilot/Processes/AuditFindings/tenantProcessStateAuditFindings';
import { TenantProcessStateAutomationDrafting } from 'models/copilot/Processes/AutomationDrafting/tenantProcessStateAutomationDrafting';
import { TenantProcessStateKpiReport } from 'models/copilot/kpiReportProcessState';
import { TenantProcessStateEntityEvidenceAudit } from 'models/copilot/entityEvidenceAudit';
import { TenantProcessStateSupplierCompliance } from 'models/copilot/Processes/SupplierCompliance/tenantProcessStateSupplierCompliance';
import { TenantProcessStateSupplierIncidents } from 'models/copilot/Processes/SupplierIncidents/tenantProcessStateSupplierIncidents';
import { TenantProcessStateControlDocumentationAudit } from 'models/copilot/Processes/ControlDocumentationAudit/copilotControlDocumentationAudit';
import { TenantProcessStateControlEvidenceAudit } from 'models/copilot/Processes/ControlEvidenceAudit/copilotControlEvidenceAudit';
import { TenantProcessStateFrameworkAudit } from 'models/copilot/Processes/FrameworkAudit/copilotFrameworkAudit';
import { TenantProcessStateLibrary } from 'models/copilot/Processes/Library/tenantProcessStateLibrary';
import { TenantProcessStateProcessSuggestions } from 'models/copilot/Processes/ProcessSuggestions/copilotProcessSuggestion';
import { TenantProcessStateTaskEvidenceAudit } from 'models/copilot/Processes/TaskEvidenceAudit/copilotTaskEvidenceAudit';
import { EntityTypes } from 'models/entity';
import ResourceList from 'models/resourceList';
import { Dispatch, SetStateAction } from 'react';
import type { ProgressHubProcess } from 'services/SignalR/ProgressHub';
import { AIActionBinding, toCopilotAction } from './actions/aiActionBinding';
import { AIActionKind, AIProcessActionTemplate } from './actions/aiActionTemplate';
import { CopilotAction } from './copilotAction';
import { CopilotPanelContentBase, CopilotPanelContext } from './copilotBridge';
import { CopilotConversation, CopilotConversationContentBlock } from './copilotConversation';
import { CopilotActiveField } from './copilotField';
import { CopilotSelectedDocument } from './copilotGeneric';
import { CopilotPanelResultBus } from './copilotPanelResultBus';
import { CopilotPreviewContent } from './copilotPreviewContent';
import { TenantProcess, TenantProcessStatus } from './tenantProcessStateBase';

export type CopilotPanelEntityRef = {
  entityType: EntityTypes;
  entityId: number;
};

export type CopilotPanelProcessStateData =
  | TenantProcessStateAnnualPlan
  | TenantProcessStateAuditFindings
  | TenantProcessStateLibrary
  | TenantProcessStateProcessSuggestions
  | TenantProcessStateControlEvidenceAudit
  | TenantProcessStateControlDocumentationAudit
  | TenantProcessStateTaskEvidenceAudit
  | TenantProcessStateFrameworkAudit
  | TenantProcessStateAutomationDrafting
  | TenantProcessStateKpiReport
  | TenantProcessStateEntityEvidenceAudit
  | TenantProcessStateSupplierIncidents
  | TenantProcessStateSupplierCompliance;

export type CopilotPanelProcessState = {
  processId: TenantProcess;
  status: TenantProcessStatus;
  modified?: Date;
  conversation: CopilotConversation;
  data: CopilotPanelProcessStateData;
};

export type CopilotPanelProgressRef = CopilotPanelEntityRef & {
  tenantProcess: TenantProcess;
  hubProcess: ProgressHubProcess;
  // The run answers in the same request that started it, so its result is already in the response the
  // definition handled: a terminal progress status must NOT trigger a persisted-state reload, which
  // would re-apply the server snapshot over the proposal the user is reviewing. Background-native
  // runs (the control audits) leave this off - for them the progress update IS the only signal.
  completesInRequest?: boolean;
  // A run that shows its result as it is made (the KPI report's charts, PLAN-070): a live progress push whose step
  // text changed reloads the persisted state when this answers true; at most one reload is in flight.
  reloadsOnProgress?: (message: string) => boolean;
};

export type CopilotPanelProcessCompletionRef = CopilotPanelEntityRef & {
  processId: TenantProcess;
};

// What a disabled chat input says while a running process waits for something other than typing: the
// conversation shell maps each value to its own literal translation key.
export enum CopilotChatInputPlaceholder {
  Working = 0,
  AnswerCard = 1,
}

export type CopilotChatInputUsageProps = {
  attachmentMode: boolean;
  canSubmit: boolean;
  hasAttachments: boolean;
  targetFolder: ResourceList | undefined;
  setShowAddDocumentModal?: (show: boolean) => void;
  setShowFolderPicker?: (show: boolean) => void;
  setShowFileUploader?: (show: boolean) => void;
  // Shown instead of "Working..." while the input is disabled because the running process waits for a card answer
  placeholder?: CopilotChatInputPlaceholder;
};

// The runtime slice the chat gate and the progress bar are decided from: the selected action and the usage states
export type CopilotPanelRuntimeView = Pick<CopilotPanelRuntime, 'action' | 'actions' | 'content' | 'getUsageState'>;

// What the person handed the panel while a process that accepts files waits at a card: the attach menu's two choices,
// a dropped file, or a library item dragged in
export type CopilotWaitingFilesInput =
  | { kind: 'chooseUpload' }
  | { kind: 'chooseLibrary' }
  | { kind: 'files'; files: File[] }
  | { kind: 'links'; linkIds: number[] };

export type CopilotPanelRuntime = {
  appContext: IAppContext;
  content: CopilotPanelContentBase | undefined;
  conversation: CopilotConversation;
  actions: CopilotAction[];
  action: CopilotAction | undefined;
  connectionId: string | null;
  accessToken: string;
  copilotSessionId?: number;
  copilotContextKey?: string;
  selectedDocument?: CopilotSelectedDocument;
  instructionExternalId?: string;
  // the field the user is editing while asking, with the text it holds right now; a field-scoped prompt only
  activeField?: CopilotActiveField;
  // the general knowledge skill the Prompt binding names; a prompt or selection turn of a binding only
  skill?: string;
  // whether the Prompt binding takes the Library's selected row as its document; a binding's turn only, so
  // unset for a typed question
  usesSelectedDocument?: boolean;
  // the title of the session a field action starts; that turn only
  sessionTitle?: string;
  // the setId of the question card the turn answers; a card's submit only
  answeredQuestionSetId?: string;
  setCopilotSessionId: Dispatch<SetStateAction<number | undefined>>;
  targetFolder?: ResourceList;
  resultBus: CopilotPanelResultBus;
  previewContent: CopilotPreviewContent;
  setPreviewContent: Dispatch<SetStateAction<CopilotPreviewContent>>;
  setConversation: Dispatch<SetStateAction<CopilotConversation | undefined>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setNewStatus: Dispatch<SetStateAction<string | undefined>>;
  mergeAdaptiveInputs: (
    currentConversation: CopilotConversation | undefined,
    incomingConversation: CopilotConversation,
  ) => CopilotConversation;
  setSharePointLibraryFromConsent: (checked: boolean, getPermissions: boolean) => Promise<void>;
  setSharePointReportFromConsent: (checked: boolean, getPermissions: boolean) => Promise<void>;
  openSharepointConsentConversation: () => void;
  submitConversation: (conversation: CopilotConversation, targetFolder?: ResourceList) => Promise<void>;
  getUsageState: <TState>(context: CopilotPanelContext) => TState | undefined;
  //the process ended server-side: stop showing it as running, without telling the server again
  endProcess: (processId: TenantProcess) => Promise<void>;
  //(re)start the panel's active action, e.g. once the preconditions of a refused start have been met
  startActiveProcess: () => Promise<void>;
  abortSignal?: AbortSignal;
};

export class CopilotPanelContextDefinition {
  context: CopilotPanelContext;

  name: string;

  supportsChat: boolean;

  supportsAttachments: boolean;

  requiresActiveAction: boolean;

  forceOpen: boolean;

  forcePinned: boolean;

  constructor() {
    this.context = CopilotPanelContext.NotAvailable;
    this.name = '';
    this.supportsChat = false;
    this.supportsAttachments = false;
    this.requiresActiveAction = false;
    this.forceOpen = false;
    this.forcePinned = false;
  }

  /**
   * The actions this usage offers for the given content: exactly these, nothing inherited. A scene
   * menu reads them directly; the panel reads them through `getActions`.
   */
  getBindings(_content: CopilotPanelContentBase | undefined): AIActionBinding[] {
    return [];
  }

  // The panel's view-model of getBindings: the Prompt and Process bindings as CopilotActions. A prompt that
  // needs a selection stays in the scene menu: the panel has no selected rows.
  getActions(t: TFunction<string[]>, content: CopilotPanelContentBase | undefined): CopilotAction[] {
    return this.getBindings(content)
      .filter((binding) => binding.template.kind !== AIActionKind.Prompt || !binding.template.requiresSelection)
      .map((binding) => toCopilotAction(binding, t))
      .filter((action): action is CopilotAction => action !== undefined);
  }

  isPersistedProcessStateReady(state: CopilotPanelProcessState): boolean {
    if (state.status === TenantProcessStatus.Completed || state.status === TenantProcessStatus.Failed) {
      return true;
    }

    return this.hasConversationContent(state.conversation);
  }

  protected hasConversationContent(conversation: CopilotConversation | undefined): boolean {
    return conversation?.thread?.some((item) => !!item.response || (item.contentBlocks?.length ?? 0) > 0) === true;
  }

  getProgressRef(_runtime: CopilotPanelRuntimeView): CopilotPanelProgressRef | undefined {
    return undefined;
  }

  /**
   * The commands the usage preview's command bar offers for the running process (the drafting process: Accept and
   * Test over the draft). Empty leaves the shared create/edit commands in place. Items run through the runtime
   * (submitConversation), never through the shell.
   */
  getPreviewCommands(_runtime: CopilotPanelRuntime, _t: TFunction<string[]>): ICommandBarItemProps[] {
    return [];
  }

  getProcessCompletionRefAfterEntitySelection(
    _runtime: Pick<CopilotPanelRuntime, 'action' | 'actions' | 'content'>,
  ): CopilotPanelProcessCompletionRef | undefined {
    return undefined;
  }

  async getAnswer(_runtime: CopilotPanelRuntime, _t: TFunction<string[]>): Promise<void> {
    return Promise.resolve();
  }

  /**
   * A usage's own handling of an adaptive-card submit, called after the submitted inputs are stored on
   * the block. Return true when the panel must run getAnswer for the card: the usage appended its own
   * conversation item and the server answers it. A card handled entirely client-side (the generic
   * definition's field proposal) returns false, as must a card the usage does not own, so the shared
   * panel behaviour is unchanged.
   */
  async onAdaptiveCardSubmit(
    _runtime: CopilotPanelRuntime,
    _block: CopilotConversationContentBlock,
    _payload: AdaptiveSubmit<JsonObject>,
    _t: TFunction<string[]>,
  ): Promise<boolean> {
    return Promise.resolve(false);
  }

  /**
   * Whether the running process takes the person's files while it waits at a card: the chat's attach and a drop then go
   * to onFilesWhileWaiting instead of the document ingest. False unless a usage opts in for its process.
   */
  acceptsFilesWhileWaiting(_runtime: CopilotPanelRuntimeView): boolean {
    return false;
  }

  // The attach or the drop at a card of a process that accepts files; called only when acceptsFilesWhileWaiting holds
  onFilesWhileWaiting(_runtime: CopilotPanelRuntime, _input: CopilotWaitingFilesInput, _t: TFunction<string[]>): void {}

  /**
   * Commits submitted card inputs onto the block of THIS runtime's conversation. The card renderer
   * commits them onto the block it rendered, which belongs to a clone of the panel state, and the
   * panel's own merge lands on yet another state object; a usage whose getAnswer re-reads the
   * conversation right after the submit needs the inputs on this object's block first.
   */
  protected applySubmittedInputs(
    runtime: CopilotPanelRuntime,
    blockId: string | undefined,
    inputs: Record<string, string>,
  ): void {
    if (!blockId) return;

    for (const threadItem of runtime.conversation.thread) {
      const target = threadItem.contentBlocks?.find((candidate) => candidate.id === blockId);
      if (!target) continue;

      target.adaptiveInputs = { ...(target.adaptiveInputs ?? {}), ...inputs };

      return;
    }
  }

  getEntityRef(_content: CopilotPanelContentBase | undefined): CopilotPanelEntityRef | undefined {
    return undefined;
  }

  // The row Stop writes Canceled on: the entity of the content, unless the running process keys its own row (the KPI
  // report on its job, PLAN-070).
  getStopRef(runtime: CopilotPanelRuntimeView, _action: CopilotAction): CopilotPanelEntityRef | undefined {
    return this.getEntityRef(runtime.content);
  }

  // Whether this usage runs the process: by default the processes of its own Process bindings for the content. The shell
  // runs a process under the active usage when it owns it, else under the Generic usage when that owns it (a process a
  // card starts on a scene of another usage, PLAN-073), never under a usage that does not.
  ownsProcess(processId: TenantProcess, content: CopilotPanelContentBase | undefined): boolean {
    return this.getBindings(content).some(
      (binding) =>
        binding.template.kind === AIActionKind.Process &&
        (binding.template as AIProcessActionTemplate<unknown>).getProcessId(binding.params) === processId,
    );
  }

  // A process a chat turn started although the usage does not list it (the KPI report from any scene, PLAN-070):
  // the action the panel follows it under, hidden from the action lists; undefined for every other process.
  getFollowedProcessAction(_processId: TenantProcess, _t: TFunction<string[]>): CopilotAction | undefined {
    return undefined;
  }

  // Whether the user may type while a process runs. The runtime view lets a usage read the running process's own
  // state (the drafting process allows input in its refinement stage only).
  canStartChat(actions: CopilotAction[], _runtime?: CopilotPanelRuntimeView): boolean {
    if (!this.supportsChat) return false;
    if (!this.requiresActiveAction) return true;

    return actions.some((action) => action.status === TenantProcessStatus.InProgress);
  }

  getChatInputUsageProps(runtime: CopilotPanelRuntime): CopilotChatInputUsageProps {
    return {
      attachmentMode: this.isAttachmentMode(runtime),
      canSubmit: false,
      hasAttachments: false,
      targetFolder: undefined,
    };
  }

  isAttachmentMode(runtime: Pick<CopilotPanelRuntime, 'actions' | 'previewContent'>): boolean {
    return (
      this.supportsAttachments &&
      runtime.previewContent === CopilotPreviewContent.None &&
      runtime.actions.some((action) => action.status === TenantProcessStatus.InProgress)
    );
  }

  prepareQuestion(question: string, _runtime: CopilotPanelRuntime, _t: TFunction<string[]>): string {
    return question;
  }

  // content: what the active scene published, so a definition shared by several scenes words the selection per scene
  getEntitySelectionQuestion(
    _selectedEntityIds: number[],
    _t: TFunction<string[]>,
    _content?: CopilotPanelContentBase,
  ): string | undefined {
    return undefined;
  }

  getWelcomeMessage(_action: CopilotAction, _t: TFunction<string[]>): string | undefined {
    return undefined;
  }

  getWelcomeMessages(action: CopilotAction, t: TFunction<string[]>): string[] {
    const message = this.getWelcomeMessage(action, t);

    return message ? [message] : [];
  }

  getCreatePreviewText(_t: TFunction<string[]>): string | undefined {
    return undefined;
  }

  canSaveDisplayState(_runtime: CopilotPanelRuntime): boolean {
    return true;
  }

  applyProcessState(runtime: CopilotPanelRuntime, state: CopilotPanelProcessState): CopilotPreviewContent | undefined {
    runtime.setConversation(state.conversation);

    return undefined;
  }

  clearPreview(_runtime: CopilotPanelRuntime): void {}

  saveDisplayState(_runtime: CopilotPanelRuntime): void {}

  discardDisplayState(_runtime: CopilotPanelRuntime): void {}

  cleanUp(_runtime: CopilotPanelRuntime): void {}

  isAvailableForContent(content: CopilotPanelContentBase | undefined): boolean {
    return content?.context === this.context;
  }

  clone(): CopilotPanelContextDefinition {
    const clone = new CopilotPanelContextDefinition();
    clone.context = this.context;
    clone.name = this.name;
    clone.supportsChat = this.supportsChat;
    clone.supportsAttachments = this.supportsAttachments;
    clone.requiresActiveAction = this.requiresActiveAction;
    clone.forceOpen = this.forceOpen;
    clone.forcePinned = this.forcePinned;

    return clone;
  }
}

export type CopilotPanelDefinitionMap = Map<CopilotPanelContext, CopilotPanelContextDefinition>;
